/*
 * File: App.tsx
 * Project: rosita
 * -----
 * Last Modified: 18 Feb 2022 3:20:12 pm
 * -----
 * Copyright (c) 2022 Elegant Software Solutions All rights reserved.
 */

import { useAuth0 } from '@auth0/auth0-react';
import { Col, Layout, Menu, MenuProps, Row, Space } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import Sider from 'antd/lib/layout/Sider';
import { CacheBuster } from 'components/common/CacheBuster';
import { DebugPanel } from 'components/common/DebugPanel';
import { Loader } from 'components/common/Loader';
import { PageHeader } from 'components/common/PageHeader';
import { ThemeProvider } from 'context/ThemeContext';
import { useAuthentication } from 'hooks/useAuthentication';
import Cookies from 'js-cookie';
import { AcuityContext } from 'models/Application';
import React, { Component, memo, ReactNode, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-use';
import { setNavSearchValue } from 'redux/slices/businessListSlice';
import { useAppSelector } from 'redux/store';
import { MainRoutes } from 'routes/MainRoutes';
import { useRenderPerformanceMonitor } from 'utils/performanceUtils';
import { InlineStylesModel } from './models/InlineStylesModel';

const { Header, Content } = Layout;

// Error boundary component
class ErrorBoundary extends Component<{ children: ReactNode; fallbackComponent: React.FC<{ error: Error; resetErrorBoundary: () => void }> }, { hasError: boolean; error: Error | null }> {
  constructor(props: { children: ReactNode; fallbackComponent: React.FC<{ error: Error; resetErrorBoundary: () => void }> }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error caught by ErrorBoundary:', error, errorInfo);
  }

  resetErrorBoundary = () => {
    this.setState({ hasError: false, error: null });
  };

  render(): React.ReactNode {
    if (this.state.hasError && this.state.error) {
      const FallbackComponent = this.props.fallbackComponent;
      return <FallbackComponent error={this.state.error} resetErrorBoundary={this.resetErrorBoundary} />;
    }
    return this.props.children;
  }
}

// Error fallback component
interface ErrorFallbackProps {
  error: Error;
  resetErrorBoundary: () => void;
}

const ErrorFallback: React.FC<ErrorFallbackProps> = ({ error, resetErrorBoundary }) => {
  const { theme } = useAppSelector((state) => state.app);
  const isDarkMode = theme === 'dark';

  const bgColor = isDarkMode ? 'rgb(31, 30, 46)' : '#f0f2f5';
  const textColor = isDarkMode ? '#ffffff' : '#000000';
  const buttonBgColor = isDarkMode ? '#1890ff' : '#1890ff';
  const buttonTextColor = '#ffffff';

  return (
    <div
      style={{
        textAlign: 'center',
        padding: '2rem',
        backgroundColor: bgColor,
        color: textColor,
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
      <h2>Something went wrong</h2>
      <p>{error.message}</p>
      <button
        onClick={resetErrorBoundary}
        style={{
          backgroundColor: buttonBgColor,
          color: buttonTextColor,
          border: 'none',
          padding: '8px 16px',
          borderRadius: '4px',
          cursor: 'pointer'
        }}>
        Try again
      </button>
    </div>
  );
};

/**
 * Helper function to get customer context from cookies
 * @returns The selected customer from cookies or undefined
 */
const getCustomerFromCookie = (): AcuityContext['selectedCustomer'] | undefined => {
  const customerCookie = Cookies.get(`${process.env.REACT_APP_COOKIE_PREFIX}_acuity_context`);

  if (customerCookie) {
    try {
      const { selectedCustomer } = JSON.parse(customerCookie) as AcuityContext;
      return selectedCustomer;
    } catch (error) {
      console.error('Error parsing customer cookie:', error);
      return undefined;
    }
  }
  return undefined;
};

// Memoized content component to prevent unnecessary re-renders
interface AppContentProps {
  contentStyle: React.CSSProperties;
}

const AppContent = memo(({ contentStyle }: AppContentProps) => {
  const { xs, sm } = useBreakpoint();
  // Monitor render performance
  useRenderPerformanceMonitor('AppContent');

  // Get theme from context
  const { theme } = useAppSelector((state) => state.app);
  const isDarkMode = theme === 'dark';

  const { user } = useAuth0();

  const isInternal = user?.['https://acuity.mdsiinc.com/user/user_metadata']?.is_mdsi_employee;

  // Theme-specific colors

  // Get current location to determine active menu item
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // State to track the selected customer
  const [selectedCustomer, setSelectedCustomer] = useState<AcuityContext['selectedCustomer'] | undefined>(undefined);
  // Reference to track the last cookie value
  const lastCookieValue = useRef<string | null>(null);

  // Check for customer cookie on initial load
  useEffect(() => {
    const customerFromCookie = getCustomerFromCookie();
    if (customerFromCookie) {
      setSelectedCustomer(customerFromCookie);
      lastCookieValue.current = Cookies.get(`${process.env.REACT_APP_COOKIE_PREFIX}_acuity_context`) || null;
    }
  }, []);

  // Set up a cookie change listener
  useEffect(() => {
    // Function to check for cookie changes
    const checkCookieChanges = () => {
      const currentCookieValue = Cookies.get(`${process.env.REACT_APP_COOKIE_PREFIX}_acuity_context`) || null;

      // Only update if the cookie value has actually changed
      if (currentCookieValue !== lastCookieValue.current) {
        lastCookieValue.current = currentCookieValue;

        const customerFromCookie = getCustomerFromCookie();
        if (customerFromCookie) {
          setSelectedCustomer(customerFromCookie);
        } else {
          setSelectedCustomer(undefined);
        }
      }
    };

    // Check frequently for cookie changes
    const intervalId = setInterval(checkCookieChanges, 200); // 200ms interval

    // Clean up interval on component unmount
    return () => clearInterval(intervalId);
  }, []);

  // Determine which menu item should be selected based on current path
  const getSelectedKey = () => {
    const pathname = location.pathname;
    if (pathname === '/internal-applications') return '2';
    if (pathname === '/') return '1';
    if (pathname === '/warehouse-applications') return '3';
    return '1'; // Default to home
  };

  // Theme-specific colors
  const bgColor = isDarkMode ? 'rgb(31, 30, 46)' : '#f0f2f5';
  const borderColor = isDarkMode ? '#2a293b' : '#e8e8e8';
  const textColor = isDarkMode ? '#fff' : '#fff';

  // Create a function to determine text color based on selection state
  const getTextColor = (key: string) => {
    const selectedKey = getSelectedKey();
    if (key === selectedKey) {
      return '#fff'; // Selected items are white in both modes
    }
    return isDarkMode ? '#fff' : '#595959'; // Non-selected items: white in dark mode, dark gray in light mode
  };

  const items2: MenuProps['items'] = [
    {
      key: '1',
      label: (
        <div>
          <Space>
            <div style={{ fontSize: '13px', color: getTextColor('1') }}>Applications{`${selectedCustomer?.name ? ` - ${selectedCustomer.name}` : ''}`}</div>
          </Space>
        </div>
      ),
      style: {
        marginTop: 0,
        borderRadius: 12,
        padding: '0px 20px'
      }
    },
    {
      key: '2',
      label: (
        <div>
          <div style={{ fontSize: '13px', color: getTextColor('2') }}>Internal Applications</div>
        </div>
      ),
      style: {
        marginTop: 0,
        borderRadius: 12,
        paddingLeft: 0,
        padding: '0px 20px'
      }
    },
    {
      key: '3',
      label: (
        <div>
          <div style={{ fontSize: '13px', color: getTextColor('3') }}>Warehouse Applications</div>
        </div>
      ),
      style: {
        marginTop: 0,
        borderRadius: 12,
        paddingLeft: 0,
        padding: '0px 20px'
      }
    }
  ];

  const secureItems = !isInternal ? items2.filter((item) => item?.key === '1') : items2;

  // Handle menu item click
  const handleMenuClick: MenuProps['onClick'] = (e) => {
    switch (e.key) {
      case '2':
        navigate('/internal-applications');
        break;
      case '1':
        navigate('/');
        break;
      case '3':
        navigate('/warehouse-applications');
        break;
      default:
        navigate('/');
    }
    dispatch(setNavSearchValue(''));
  };

  // Update content style based on theme
  const themedContentStyle = {
    ...contentStyle,
    backgroundColor: bgColor
  };

  return (
    <Layout style={{ backgroundColor: bgColor }}>
      <Content style={themedContentStyle}>
        <Suspense fallback={<Loader isDarkMode={isDarkMode} fullHeight />}>
          <Row style={{ minHeight: '100vh' }}>
            {!xs && (
              <Col xs={24} sm={8} md={5} lg={4} xl={3} xxl={3}>
                <Layout style={{ borderRight: `1px solid ${borderColor}`, height: '100%', background: bgColor }}>
                  <Sider
                    style={{
                      background: bgColor,
                      height: '100%',
                      transition: 'none'
                    }}
                    theme={'dark'}>
                    <Menu
                      theme={'dark'}
                      mode="inline"
                      selectedKeys={[getSelectedKey()]}
                      defaultOpenKeys={['sub1']}
                      style={{
                        borderRight: 0,
                        padding: '24px 10px',
                        background: bgColor,
                        transition: 'none'
                      }}
                      items={secureItems}
                      onClick={handleMenuClick}
                    />
                  </Sider>
                </Layout>
              </Col>
            )}
            <Col xs={24} sm={16} md={19} lg={20} xl={21} xxl={21}>
              <Layout style={{ height: '100%', width: '100%', padding: xs ? 8 : 24, background: bgColor }}>
                <MainRoutes />
              </Layout>
            </Col>
          </Row>
        </Suspense>
      </Content>
    </Layout>
  );
});

const App = memo(() => {
  const { xs } = useBreakpoint();
  const { theme } = useAppSelector((state) => state.app);
  const isDarkMode = theme === 'dark';

  const bgColor = isDarkMode ? 'rgb(31, 30, 46)' : '#f0f2f5';
  const textColor = isDarkMode ? '#84859a' : '#595959';
  const headerBgColor = isDarkMode ? '#1f1e2e' : '#ffffff';

  // Memoize styles to prevent recalculation on each render
  const styles: InlineStylesModel = useMemo(
    () => ({
      layout: {
        height: '100%',
        minHeight: '100vh'
      },
      header: {
        lineHeight: 1,
        display: 'flex',
        justifyContent: 'center',
        height: 110,
        color: textColor,
        backgroundColor: headerBgColor
      },
      content: {
        padding: xs ? '10px 20px' : '0px 0px',
        // maxWidth: 1920,
        width: '100%',
        minHeight: '100vh',
        backgroundColor: bgColor
      },
      somethingWrong: {
        textAlign: 'center',
        height: '100vh'
      }
    }),
    [xs, isDarkMode, textColor, headerBgColor, bgColor]
  );

  const { accessToken, isLoading } = useAuthentication();

  // Show loader while authentication is in progress
  if (isLoading || accessToken === null) {
    return <Loader isDarkMode={isDarkMode} fullHeight />;
  }

  return (
    <ThemeProvider>
      <ErrorBoundary fallbackComponent={ErrorFallback}>
        <Suspense fallback={<Loader />}>
          <CacheBuster checkInterval={60000} />
          <DebugPanel />
          <PageHeader />
          <AppContent contentStyle={styles.content} />
        </Suspense>
      </ErrorBoundary>
    </ThemeProvider>
  );
});

export default App;
